import { Sprite, Container } from 'pixi.js';
import type { BlockerView } from '../../types';
import { PlankBlockerBaseLayout } from '../../../../../layouts/blockers/PlankBlockerBaseLayout';
import { PlankBlockerPlankLayout } from '../../../../../layouts/blockers/PlankBlockerPlankLayout';
import type { PlankBlockerModel } from '../../models/blockers/PlankBlockerModel';
import { shatterAnimation } from './shatterAnimation';
import { soundManager } from '../../../../app/SoundManager';
import { TileColor } from '../../TileColor';

const layersConfig = [
  {
    idleSprite: 'Plank_1',
    shatterLayout: PlankBlockerBaseLayout,
    scaleX: 1,
  },
  {
    idleSprite: 'Plank_2',
    shatterLayout: PlankBlockerPlankLayout,
    scaleX: 1,
  },
  {
    idleSprite: 'Plank_3',
    shatterLayout: PlankBlockerPlankLayout,
    scaleX: 1,
  },
];

const colorToPrefix: Record<TileColor, string> = {
  [TileColor.None]: '',
  [TileColor.Red]: `_Red`,
  [TileColor.Green]: `_Green`,
  [TileColor.Blue]: `_Blue`,
  [TileColor.Pink]: `_Pink`,
  [TileColor.Yellow]: `_Yellow`,
  [TileColor.Grey]: `_Grey`,
  [TileColor.White]: `_White`,
  [TileColor.Navy]: `_Navy`,
  [TileColor.Purple]: `_Purple`,
};

export class PlankBlockerView extends Container implements BlockerView {
  private readonly layers: Sprite[] = [];

  private hp = 3;

  constructor(private readonly model: PlankBlockerModel) {
    super();
    this.setHp(model.getHp());
  }

  public getModel(): PlankBlockerModel {
    return this.model;
  }

  public onModelChanged(): void {
    if (this.hp - 1 === this.model.getHp()) {
      void this.animateHit();
    } else {
      this.setHp(this.model.getHp());
    }
  }

  public async animateRemoval(): Promise<void> {
    await this.animateHit();
    this.destroy({ children: true });
  }

  private setHp(hp: number): void {
    if (hp < 1 || hp > 3) return;

    this.layers.forEach((layer) => {
      layer.destroy({ children: true });
    });
    this.layers.length = 0;

    for (let i = 0; i < hp; i += 1) {
      const config = layersConfig[i];
      if (!config) break;

      this.layers[i] = new Sprite().props({
        parent: this,
        texture: config.idleSprite + this.getColorSuffix(),
        anchor: 0.5,
        scaleX: config.scaleX ?? 1,
      });
    }

    this.hp = hp;
  }

  private getColorSuffix(): string {
    return colorToPrefix[this.model.getColor()];
  }

  private async animateHit(): Promise<void> {
    this.hp -= 1;

    const sprite = this.layers[this.hp];
    this.layers.length = this.hp;

    if (!sprite) return;

    await this.tweenTo({ scaleX: 0.8, scaleY: 0.8 }, 100, 'cubicOut', true).asyncOnComplete();

    const config = layersConfig[this.hp];

    sprite.destroy();
    // eslint-disable-next-line new-cap
    const shatterLayout = new config.shatterLayout();
    shatterLayout.root.props({
      parent: this,
    });

    shatterLayout.root.children.forEach((child) => {
      if (child instanceof Sprite) {
        child.props({
          texture: child.texture.label + this.getColorSuffix(),
        });
      }
    });

    soundManager.play('Gameplay_Breaking_Plank');

    this.tweenTo({ scaleX: 1, scaleY: 1 }, 100, 'appear1', true);
    await shatterAnimation(shatterLayout.root.children);
    shatterLayout.root.destroy({ children: true });
  }
}
